import Component from "sap/ui/core/Component";
import Controller from "sap/ui/core/mvc/Controller";
import Router, {Router$RouteMatchedEvent} from "sap/ui/core/routing/Router";
import UIComponent from "sap/ui/core/UIComponent";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import JSONModel from "sap/ui/model/json/JSONModel";
import History from "sap/ui/core/routing/History";
import MessageToast from "sap/m/MessageToast";
import {Model$PropertyChangeEvent, Model$RequestFailedEvent} from "sap/ui/model/Model";
import {FileUploader$ChangeEvent} from "sap/ui/unified/FileUploader";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import RootComponent from "niteroi/Component";
import Table from "sap/m/Table";
import Tree from "sap/m/Tree";
import ListBinding from "sap/ui/model/ListBinding";
import {Button$PressEvent} from "sap/m/Button";
import UiTable from "sap/ui/table/Table";
import MenuItem, {MenuItem$PressEvent} from "sap/m/MenuItem";
import {SharedServices} from "niteroi/services/SharedServices";
import {Menu$ItemSelectedEvent} from "sap/m/Menu";
import Theming from "sap/ui/core/Theming";
import {FileUploader$FileSizeExceedEvent} from "sap/ui/commons/FileUploader";
import {InputBase$ChangeEvent} from "sap/m/InputBase";
import Validation from "niteroi/packages/system/helpers/Validation";
import {IVIESResponse} from "niteroi/packages/system/models/interfaces";
import Logger from "sap/base/Log";

/**
 * Shared base controller for all packages
 * Provides common functionality across the application
 *
 * @namespace niteroi.packages.system.controllers
 */
export default abstract class BaseController extends Controller {
    private _resourceBundle: ResourceBundle | undefined;
    protected isModelSaved: boolean = true;
    protected originalData: object = {};

    public onInit(): void {
        Promise.resolve(this.getResourceBundle()).then(bundle => {
            this._resourceBundle = bundle;
        });
    }

    public onBeforeRendering(): void {
        const router = this.getRouter();
        router.attachRouteMatched(this.onRouteMatchedForSelectedItem.bind(this), this);
    }

    public onAfterRendering(): void {
        const router = this.getRouter();
        router.detachRouteMatched(this.onRouteMatchedForSelectedItem.bind(this), this);
    }

    private onRouteMatchedForSelectedItem(event: Router$RouteMatchedEvent): void {
        const routeName = event.getParameter("name");
        let routeOrHash = routeName;
        if (routeName.includes("/") === false) {
            const hash = event.getSource().getHashChanger().getHash();
            if (hash.includes("/")) {
                routeOrHash = hash;
            }
        }
        if (routeOrHash) {
            const routeParts = routeOrHash.split("/");
            const packageName = routeParts[0];
            const componentName = routeParts[1];
            this.setSelectedSideNavigationItem(packageName, componentName);
        } else {
            this.setSelectedSideNavigationItem(routeName);
        }
    }

    /**
     * Convenience method for accessing the router in every controller.
     * The owner component in this case is the package's Component, not the root Component.
     * This allows each package to have its own routing configuration and still use this common base controller.
     */
    public getRouter(): Router {
        return (this.getOwnerComponent() as UIComponent).getRouter();
    }

    protected getCurrentRouteName(router = this.getRouter()): string {
        const currentHash = router.getHashChanger().getHash();
        if (currentHash) {
            const routeInfo = router.getRouteInfoByHash(currentHash);
            return routeInfo?.name ?? "";
        }
        return "";
    }

    /**
     * Convenience method for getting the view model by name
     */
    public getModel(name?: string): JSONModel | undefined {
        const view = this.getView();
        let model: JSONModel;
        if (view) {
            const viewModel = view.getModel(name) as JSONModel;
            if (viewModel) {
                model = viewModel;
            }
        }

        let component: Component | null | undefined = this.getOwnerComponent();
        while (component) {
            const componentModel = component.getModel(name) as JSONModel;
            if (componentModel) {
                model = componentModel;
                break;
            }
            component = Component.getOwnerComponentFor(component) ?? null;
        }
        if (model) {
            model.setSizeLimit(9999);
            model.setDefaultBindingMode("TwoWay");
            model.detachRequestFailed(this.fnRequestFailed, this);
            model.attachRequestFailed(this.fnRequestFailed, this);
        }
        return model;
    }

    private fnRequestFailed(event: Model$RequestFailedEvent): void {
        const responseText = event.getParameter("responseText");
        let data: string | { error: string; code: number };
        try {
            data = JSON.parse(responseText);
        } catch (error) {
            Logger.info(JSON.stringify(error));
            data = responseText;
        }
        const invalidSessionCodes = [400, 401, 410, 425, 451];
        const statusCode = event.getParameter("statusCode") as number;
        if (invalidSessionCodes.includes(Number(statusCode))) {
            window.localStorage.removeItem("sessionId");
            this.getModel("session").setData({});
            this.navTo("start", {}, true);
            if (data) {
                if (typeof data === "object") {
                    this.showToast(data.error);
                } else {
                    this.showToast(data);
                }
            }
        }

        if (Number(statusCode) === 405 || Number(statusCode) >= 500) {
            this.navTo("500", {}, true);
        }
        if (Number(statusCode) === 404) {
            this.navTo("404", {}, true);
        }
    }

    /**
     * Convenience method for setting the view model
     */
    public setModel(model: JSONModel, name?: string): void {
        // Walk the ownership chain to find where this named model lives
        let component: Component | null | undefined = this.getOwnerComponent();
        while (component) {
            if (component.getModel(name)) {
                component.setModel(model, name);
                return;
            }
            component = Component.getOwnerComponentFor(component) ?? null;
        }

        // Not found in ownership chain — set on the view
        this.getView()?.setModel(model, name);
    }

    /**
     * Convenience method for getting the resource bundle
     */
    public getResourceBundle(): ResourceBundle | Promise<ResourceBundle> {
        const model = this.getOwnerComponent().getModel("i18n") as ResourceModel;
        return model.getResourceBundle();
    }

    /**
     * Create a new JSON model with request configuration
     */
    public createRequestModel(defaultData: object = {}): JSONModel {
        const model = new JSONModel(defaultData);
        model.setSizeLimit(9999);
        model.setDefaultBindingMode("TwoWay");
        model.attachRequestFailed(() => {
            const data = model.getData();
            if (data && [401, 403, 405].includes(data.code)) {
                window.localStorage.removeItem("sessionId");
                this.navTo("start", {}, true);
                this.showToast(data.error);
            } else if (data && data.error) {
                this.showToast(data.error);
            }
        });
        return model;
    }

    public getRequestHeaders(): object {
        const sessionId: string = window.localStorage.getItem("sessionId") ?? "";
        return {
            "Authorization": "Bearer " + sessionId,
            "Content-Type": "application/json;charset=utf-8",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": "Fetch"
        };
    }

    /**
     * Navigate to previous page in history or to a fallback route
     */
    public navBack(fallbackRoute: string = "home"): void {
        const history = History.getInstance();
        const previousHash = history.getPreviousHash();

        if (previousHash !== undefined) {
            window.history.go(-1);
        } else {
            this.navTo(fallbackRoute, {}, true);
        }
    }

    /**
     * Get translation text from i18n bundle
     */
    public getText(key: string, args?: Array<string|number>): string {
        return this._resourceBundle?.getText(key, args) ?? key;
    }

    /**
     * Show busy indicator
     */
    public setBusy(busy: boolean): void {
        this.getView().setBusy(busy);
    }

    protected setSelectedSideNavigationItem(packageName: string = "system", componentName: string = "launchpad"): void {
        const selectedSideNavigationModel = this.getModel("selectedSideNavigationKey");

        if (!selectedSideNavigationModel) {
            this.setModel(new JSONModel({key: `${packageName}-${componentName}`}), "selectedSideNavigationKey");
        } else {
            selectedSideNavigationModel.setProperty("/key", `${packageName}-${componentName}`);
        }
        this.setExpandedSideNavigationItem(packageName);
    }

    private setExpandedSideNavigationItem(key: string): void {
        const activeSideNavigationModel = this.getModel("activeSideNavigationItem");
        if (!activeSideNavigationModel) {
            this.setModel(new JSONModel({ item: key }), "activeSideNavigationItem");
        } else {
            activeSideNavigationModel.setProperty("/item", key);
        }
    }

    /**
     * Navigate to a specific target.
     * @public
     * @param routeHash Target name
     * @param [parameters] Navigation parameters
     * @param [replace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
     */
    public navTo(routeHash: string, parameters: object = {}, replace: boolean = true): void {
        const packageComponent = this.getOwnerComponent() as RootComponent;
        if (packageComponent && packageComponent.getRouteServices) {
            this.getView().setBusy(true);
            packageComponent.getRouteServices().navTo(routeHash, parameters, replace).then(() => {
                const routeParts = routeHash.split("/");
                const packageName = routeParts.length > 0 ? routeParts.reverse().pop() : routeHash;
                const componentTarget = routeParts.length > 0 ? routeParts.pop() : "";
                this.setSelectedSideNavigationItem(packageName, componentTarget);
            }).finally(() => {
                this.getView().setBusy(false);
            });
        }
    }

    public uuidv4(): string {
        return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
            (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
        );
    }

    public validateInputEmail(event: InputBase$ChangeEvent): void {
        const source = event.getSource();
        const value = event.getParameter("value");
        if (! Validation.isValidEmail(value)) {
            source.setValueState("Error");
            return;
        }
        source.setValueState("None");
    }

    public resetOriginalModel(modelName: string = ""): void {
        if (modelName) {
            this.isModelSaved = true;
            this.originalData = {};
            const model = new JSONModel({}, true);
            model.setData(Object.assign({}, this.originalData, {isModelSaved: this.isModelSaved}));
            this.setModel(model, modelName);

            model.attachPropertyChange(this.setModelEditMode.bind(this));
        }
    }

    public setModelEditMode(event: Model$PropertyChangeEvent): void {
        this.isModelSaved = false;
        this.originalData = {};
        (event.getSource() as JSONModel).setProperty("/isModelSaved", this.isModelSaved);
    }

    public discardChanges(modelName: string = ""): void {
        const model = this.getModel(modelName);
        model.setData(this.originalData);
        this.originalData = Object.assign({}, JSON.parse(model.getJSON()));
    }

    public onPressButtonDeleteFormEdit(event: Button$PressEvent): void {
        this.showToast(`Delete not implemented yet. ${event.getSource().getText()}`);
    }

    public showToast(message: string, duration: number = 10000, onCloseCallback: () => void = () => {}): void {
        MessageToast.show(message, {
            my: "EndTop",
            at: "EndTop",
            of: window,
            offset: "-120 30",
            collision: "fit flip",
            autoClose: true,
            duration: duration,
            onClose: onCloseCallback,
            closeOnBrowserNavigation: true,
            width: "auto"
        });
    }

    public onNavButtonPress(): void {
        this.navBack();
    }

    public onSelectTheme(event: Menu$ItemSelectedEvent): void {
        const themeKey = (event.getParameter("item") as MenuItem).getKey();
        const model = this.getModel("theme");
        window.localStorage.setItem("userTheme", themeKey);
        const themeName = SharedServices.getThemeName(themeKey);
        model.setProperty("/theme", themeName);
        Theming.setTheme(themeName);
    }

    public onPressLanguageMenuItem(event: MenuItem$PressEvent): void {
        const menuItem = event.getSource();
        const selectedLanguage = menuItem.getKey();
        SharedServices.updateLanguage(selectedLanguage, this.getOwnerComponent() as UIComponent);
        this.getModel("pricing").refresh();
    }

    public onFileSizeExceed(event: FileUploader$FileSizeExceedEvent): void {
        const message = this.getText("msgAvatarFileExceeds", [
            event.getParameter("fileName"),
            event.getSource().getMaximumFileSize(),
            Number(event.getParameter("fileSize")).toFixed(2)
        ]);
        this.showToast(message)
    }

    public onChangeFileUploaderLogo(event: FileUploader$ChangeEvent): void {
        event.preventDefault();

        const controllerName = this.getMetadata().getName().split(".").pop();
        const modelName = controllerName === "Users" ? "user" : "company";
        const propertyPath = controllerName === "Users" ? "/avatar" : "/logo";

        const files = event.getParameters().files as Array<File>;
        const fileReader = new FileReader();
        fileReader.onload = (result) => {
            const base64 = result.target?.result as string;
            const model = this.getModel(modelName);
            model.setProperty(propertyPath, base64);
        };
        fileReader.readAsDataURL(files[0]);
    }

    public verifyVatWithVies(
        rawVat: string
    ): IVIESResponse {
        const formatCheck = Validation.validateEUVatFormat(rawVat);

        if (!formatCheck.isValid || !formatCheck.countryCode || !formatCheck.cleanVatNumber) {
            return {
                valid: false,
                countryCode: formatCheck.countryCode,
                vatNumber: formatCheck.cleanVatNumber,
                userError: formatCheck.error ?? 'Invalid VAT syntax',
            };
        }
        return { valid: true, countryCode: formatCheck.countryCode, vatNumber: formatCheck.cleanVatNumber };
    }

    public onSearchList(event: SearchField$LiveChangeEvent, tableName: string, fields: string[]): void {
        const query = event.getParameter("newValue");
        const list = this.byId(tableName) as Table | Tree | UiTable;
        const binding = (list.getBinding("items") || list.getBinding("rows")) as ListBinding;

        if (!binding) {
            return;
        }

        if (!query) {
            binding.filter([]);
            return;
        }

        const filters = fields.map((field) => {
            return new Filter(field, FilterOperator.Contains, query);
        });

        binding.filter(new Filter({
            filters: filters,
            and: false
        }));
    }
}
